import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProfileProviderPJ, ProfileProviderPjControllerService } from '../../core/generated';

@Injectable({
  providedIn: 'root'
})
export class ProfileProviderPJCacheListService {
  private profileProviderPJService = inject(ProfileProviderPjControllerService);
  private profileProviderPJListSubject = new BehaviorSubject<ProfileProviderPJ[] | null>(null);
  private profileProviderPJCache: { [id: string]: ProfileProviderPJ } = {};

  constructor() {
    // Recupera cache do localStorage ao iniciar
    const cache = localStorage.getItem('profileProviderPJCache');
    if (cache) {
      this.profileProviderPJCache = JSON.parse(cache);
    }
  }

  get profileProviderPJList$(): Observable<ProfileProviderPJ[] | null> {
    return this.profileProviderPJListSubject.asObservable();
  }

  loadAll(): void {
    this.profileProviderPJService.findAll1().subscribe({
      next: (list) => {
        this.profileProviderPJListSubject.next(list);
        if (list) {
          // Atualiza cache local
          for (const pj of list) {
            if (pj.id !== undefined) {
              this.profileProviderPJCache[pj.id] = pj;
            }
          }
          localStorage.setItem('profileProviderPJCache', JSON.stringify(this.profileProviderPJCache));
        }
        localStorage.setItem('profileProviderPJList', JSON.stringify(list));
      },
      error: () => {
        const cache = localStorage.getItem('profileProviderPJList');
        if (cache) {
          this.profileProviderPJListSubject.next(JSON.parse(cache));
        }
      }
    });
  }

  getFromCache(): ProfileProviderPJ[] | null {
    const cache = localStorage.getItem('profileProviderPJList');
    return cache ? JSON.parse(cache) : null;
  }

  /**
   * Busca um ProfileProviderPJ pelo id.
   * Se não estiver no cache, busca no backend, adiciona ao cache e retorna via callback.
   * Sempre use o callback para obter o resultado.
   */
  findProfileProviderPJById(id: number, callback?: (pj: ProfileProviderPJ | undefined) => void): ProfileProviderPJ | undefined {
    if (!id) {
      if (callback) callback(undefined);
      return undefined;
    }
    // Busca em memória
    if (this.profileProviderPJCache[id]) {
      if (callback) callback(this.profileProviderPJCache[id]);
      return this.profileProviderPJCache[id];
    }
    // Busca no localStorage
    const cache = localStorage.getItem('profileProviderPJCache');
    if (cache) {
      const parsed = JSON.parse(cache);
      if (id) {
        const profilePJ = parsed[id] as ProfileProviderPJ;
        if (profilePJ) {
          this.profileProviderPJCache[id] = profilePJ;
          if (callback) callback(profilePJ);
          return profilePJ;
        }
        if (callback) callback(undefined);
        return undefined;
      }
    }
    // Busca no backend e adiciona ao cache/localStorage (assíncrono)
    this.profileProviderPJService.findById6(id).subscribe({
      next: (company) => {
        if (company) {
          this.profileProviderPJCache[id] = company;
          localStorage.setItem('profileProviderPJCache', JSON.stringify(this.profileProviderPJCache));
          if (callback) callback(company);
        } else {
          if (callback) callback(undefined);
        }
      },
      error: () => {
        if (callback) callback(undefined);
      }
    });
    return undefined;
  }

  findProfileProviderPJByEmail(email: string): ProfileProviderPJ | undefined {
    if (!email) return undefined;
    // Busca em memória
    for (const pj of Object.values(this.profileProviderPJCache)) {
      if (pj.email === email) return pj;
    }
    // Busca no localStorage
    const cache = localStorage.getItem('profileProviderPJCache');
    if (cache) {
      const parsed = JSON.parse(cache);
      for (const pj of Object.values(parsed)) {
        const profilePJ = pj as ProfileProviderPJ;
        if (profilePJ.email === email) {
          if (profilePJ.id !== undefined) {
            this.profileProviderPJCache[profilePJ.id] = profilePJ;
          }
          return profilePJ;
        }
      }
    }
    return undefined;
  }

  findProfileProviderPJByCnpj(cnpj: string, callback?: (pj: ProfileProviderPJ | undefined) => void): ProfileProviderPJ | undefined {
    if (!cnpj) {
      if (callback) callback(undefined);
      return undefined;
    }
    // Busca em memória
    for (const pj of Object.values(this.profileProviderPJCache)) {
      if (pj.cnpj === cnpj) {
        if (callback) callback(pj);
        return pj;
      }
    }
    // Busca no localStorage
    const cache = localStorage.getItem('profileProviderPJCache');
    if (cache) {
      const parsed = JSON.parse(cache);
      for (const pj of Object.values(parsed)) {
        const profilePJ = pj as ProfileProviderPJ;
        if (profilePJ.cnpj === cnpj) {
          if (profilePJ.id !== undefined) {
            this.profileProviderPJCache[profilePJ.id] = profilePJ;
          }
          if (callback) callback(profilePJ);
          return profilePJ;
        }
      }
    }
    // Busca no backend e adiciona ao cache/localStorage (assíncrono)
    this.profileProviderPJService.findByCnpj2(cnpj).subscribe({
      next: (company) => {
        if (company) {
          if (company.id !== undefined) {
            this.profileProviderPJCache[company.id] = company;
            localStorage.setItem('profileProviderPJCache', JSON.stringify(this.profileProviderPJCache));
          }
          if (callback) callback(company);
        } else {
          if (callback) callback(undefined);
        }
      },
      error: () => {
        if (callback) callback(undefined);
      }
    });
    return undefined;
  }

  addProfileProviderPJById(id: number, callback?: (pj: ProfileProviderPJ | undefined) => void): void {
    this.findProfileProviderPJById(id, callback);
  }
}
